/**
 * Global Organization Manager — service layer
 * ============================================
 *
 * Encapsulates the cross-collection writes used by the
 * GlobalOrganizationManager admin page. Each operation:
 *
 *   1. Updates the canonical doc.
 *   2. Cascades to the link doc (/broadcaster or /mediaowner).
 *   3. Updates per-user denormalised snapshots in /users/{uid}.organizations[].
 *   4. Writes one append-only entry to /orgChangeLog.
 *
 * The page never writes directly — it goes through these helpers so the
 * audit trail is consistent.
 *
 * Vocabulary
 * ----------
 *   - "Entity" = a media owner or broadcaster as the page sees it: an
 *     /organizations doc plus its matching /broadcaster or /mediaowner
 *     link doc. The two halves are tracked by `linkedOrgId === orgId`.
 *   - "Owner" = the user assigned ADMIN on the org. Created on org-create
 *     and editable via assignOwner().
 *
 * Ownership / permissions
 * -----------------------
 * Callers are expected to gate on `isMasterOrg && currentOrgRole === 'ADMIN'`.
 * Firestore rules enforce server-side.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  ORGANIZATIONSPATH,
  BROADCASTERPATH,
  MEDIAOWNERPATH,
  USERSPATH,
  AGENCYPATH,
  CLIENTPATH,
  GLOBAL_ARTISTPATH,
} from '../constants/paths';
import { writeChangeLog } from './orgChangeLog';
import { categoryToType, OrgCategory } from '../store/organization.store';

// --- Types ----------------------------------------------------------------

/**
 * What kind of entity a Global Organization Manager row represents.
 *
 *   - 'broadcaster' — has a /broadcaster link doc; parent media house + languages.
 *   - 'mediaowner'  — has a /mediaowner link doc.
 *   - 'org'         — bare /organizations doc (artist, agency, brand, etc.)
 *                     with no link collection. linkId is the empty string.
 */
export type EntityKind = 'broadcaster' | 'mediaowner' | 'org';

export interface EntityDoc {
  /** /organizations doc id */
  orgId: string;
  /** /broadcaster or /mediaowner link doc id (empty string for 'org' kind) */
  linkId: string;
  /** /organizations.name (the canonical display name) */
  name: string;
  /** /organizations.category (e.g. 'broadcaster' | 'mediahouse' | 'agency' | ...) */
  category: string | null;
  /** /organizations.type */
  type: string;
  /** Only present on /broadcaster entities */
  parentMediaOwnerOrgId?: string | null;
  parentMediaOwnerName?: string | null;
  acceptedLanguages?: string[];
  /** subType / province / etc. — passthrough for display */
  subType?: string | null;
  province?: string | null;
  /** The link doc's raw value field, useful for the slug column */
  value?: string;
}

// --- Helpers --------------------------------------------------------------

const slugify = (name: string) =>
  String(name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/**
 * Maps an OrgCategory to the root-level "global lookup" collection that
 * dropdowns / autocompletes read from. Mirrors the legacy GetStarted flow's
 * `firebase.createGlobalEntryForOrg()` mapping so the GOM-created entries
 * are indistinguishable from anything the old self-service flow produced.
 *
 *   artist, publisher          → /artists-global
 *   agency                     → /agency
 *   brand, production          → /client
 *   broadcaster, mediahouse    → null (handled via the broadcaster/mediaowner
 *                                      branch in createEntity, not here)
 *
 * Returns `null` for categories whose link doc is already written elsewhere
 * (broadcaster/mediahouse) so callers can short-circuit.
 */
const orgCategoryToGlobalCollection = (category: string): string | null => {
  switch (category) {
    case 'artist':
    case 'publisher':
      return GLOBAL_ARTISTPATH;
    case 'agency':
      return AGENCYPATH;
    case 'brand':
    case 'production':
      return CLIENTPATH;
    default:
      return null;
  }
};

// --- Loaders --------------------------------------------------------------

/** Loads the full set of entities the Global Organization Manager renders:
 *  broadcasters + media owners (joined to their link doc) plus every other
 *  /organizations doc that has no corresponding broadcaster/mediaowner link.
 *  The "otherOrgs" bucket covers artists, agencies, brands, etc. — anything
 *  that lives only in /organizations. */
export async function loadEntities(db: any): Promise<{
  broadcasters: EntityDoc[];
  mediaowners: EntityDoc[];
  otherOrgs: EntityDoc[];
  /** Map orgId → org doc data, for quick lookups in the UI. */
  orgIndex: Record<string, any>;
}> {
  const [orgsSnap, bcSnap, moSnap] = await Promise.all([
    getDocs(collection(db, ORGANIZATIONSPATH)),
    getDocs(collection(db, BROADCASTERPATH)),
    getDocs(collection(db, MEDIAOWNERPATH)),
  ]);
  const orgIndex: Record<string, any> = {};
  for (const d of orgsSnap.docs) orgIndex[d.id] = { id: d.id, ...d.data() };

  const join = (d: any, kind: EntityKind): EntityDoc | null => {
    const data = d.data();
    const orgId = data.linkedOrgId;
    if (!orgId || !orgIndex[orgId]) return null; // orphan link, hidden from UI
    const org = orgIndex[orgId];
    const base: EntityDoc = {
      orgId,
      linkId: d.id,
      name: org.name || data.label || '(unnamed)',
      category: org.category || null,
      type: org.type || '',
      value: data.value,
      acceptedLanguages: Array.isArray(data.acceptedLanguages) ? data.acceptedLanguages : [],
      subType: data.subType || null,
      province: data.province || null,
    };
    if (kind === 'broadcaster') {
      base.parentMediaOwnerOrgId = data.parentMediaOwnerOrgId || null;
      base.parentMediaOwnerName = data.parentMediaOwnerName || null;
    }
    return base;
  };

  const broadcasters: EntityDoc[] = bcSnap.docs.map((d) => join(d, 'broadcaster')).filter(Boolean) as EntityDoc[];
  const mediaowners: EntityDoc[] = moSnap.docs.map((d) => join(d, 'mediaowner')).filter(Boolean) as EntityDoc[];

  // "Other" orgs — every /organizations doc that ISN'T linked from a
  // /broadcaster or /mediaowner link. These are bare-org entities (artists,
  // agencies, brands, productions, publishers, etc.) that the Global
  // Organization Manager surfaces in its third tab.
  const linkedOrgIds = new Set<string>([
    ...broadcasters.map((b) => b.orgId),
    ...mediaowners.map((m) => m.orgId),
  ]);
  const otherOrgs: EntityDoc[] = orgsSnap.docs
    .filter((d) => !linkedOrgIds.has(d.id))
    .map((d) => {
      const data = d.data();
      const entity: EntityDoc = {
        orgId: d.id,
        linkId: '',
        name: data.name || '(unnamed)',
        category: data.category || null,
        type: data.type || '',
      };
      return entity;
    });

  return { broadcasters, mediaowners, otherOrgs, orgIndex };
}

/** Lists every user who holds the ADMIN role on at least one Master Org.
 *  Used by the "Add as ADMIN" picker in the Global Organization Manager.
 *  A user is eligible only if their /users/{uid}.organizations[] contains
 *  an entry where orgId points at an org with isMasterOrg === true AND
 *  the entry's role is 'ADMIN'. Lower-role members of master orgs (OPERATOR
 *  / USER) are deliberately excluded. */
export async function loadOwnerCandidates(db: any): Promise<Array<{
  uid: string;
  displayName: string;
  email: string;
}>> {
  // Identify master org IDs first.
  const orgsSnap = await getDocs(query(
    collection(db, ORGANIZATIONSPATH),
    where('isMasterOrg', '==', true),
  ));
  const masterOrgIds = new Set(orgsSnap.docs.map((d) => d.id));
  if (masterOrgIds.size === 0) return [];

  // Walk /users and surface anyone with an ADMIN role on at least one master org.
  const usersSnap = await getDocs(collection(db, USERSPATH));
  const out: Array<{ uid: string; displayName: string; email: string }> = [];
  for (const u of usersSnap.docs) {
    const data = u.data();
    const orgs = Array.isArray(data.organizations) ? data.organizations : [];
    const isMasterAdmin = orgs.some((o: any) =>
      o && masterOrgIds.has(o.orgId) && o.role === 'ADMIN');
    if (isMasterAdmin) {
      out.push({
        uid: u.id,
        displayName: data.displayName || data.username || data.email || u.id,
        email: data.email || '',
      });
    }
  }
  return out.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

// --- Mutations ------------------------------------------------------------

/** Creates a new entity end-to-end: org doc + link doc + member doc for the
 *  owner + user array entry + change-log entry. */
export async function createEntity(db: any, params: {
  kind: EntityKind;
  name: string;
  category: string; // 'broadcaster' | 'mediahouse' | etc.
  ownerUid: string;
  ownerName?: string;
  ownerEmail?: string;
  parentMediaOwnerOrgId?: string | null;
  acceptedLanguages?: string[];
  actorUid: string;
  actorName?: string;
}): Promise<{ orgId: string; linkId: string }> {
  const now = new Date().toISOString();
  const trimmedName = String(params.name).replace(/\s+/g, ' ').trim();
  if (!trimmedName) throw new Error('Name is required');
  if (!params.ownerUid) throw new Error('Owner is required');

  // 1) Create the organization doc. Top-level `type` is derived from the
  //    chosen category so the two stay consistent — see the OrgCategory
  //    docs in src/store/organization.store.ts.
  const orgRef = doc(collection(db, ORGANIZATIONSPATH));
  const orgId = orgRef.id;
  const orgPayload: any = {
    id: orgId,
    name: trimmedName,
    category: params.category,
    type: categoryToType(params.category as OrgCategory),
    createdAt: now,
    createdBy: params.actorUid,
    ftpEnabled: false,
    settings: { allowUserInvites: false, defaultUserRole: 'OPERATOR' },
  };
  await setDoc(orgRef, orgPayload);

  // 2) For broadcaster/mediaowner: create the matching link doc in
  //    /broadcaster or /mediaowner. For 'org' kind: write a global lookup
  //    entry into /agency, /client, or /artists-global depending on the
  //    org's category — that's what dropdowns (RapidUpload agency picker,
  //    chase list client picker, share/request license modals) read from.
  //    artist/publisher are written to artists-global; brand/production
  //    share /client; agency goes to /agency.
  let linkRefId = '';
  if (params.kind !== 'org') {
    const linkColl = params.kind === 'broadcaster' ? BROADCASTERPATH : MEDIAOWNERPATH;
    const linkRef = doc(collection(db, linkColl));
    const linkPayload: any = {
      createdAt: Date.now(),
      label: trimmedName,
      linkedAt: Date.now(),
      linkedOrgId: orgId,
      value: slugify(trimmedName),
    };
    if (params.kind === 'broadcaster') {
      linkPayload.parentMediaOwnerOrgId = params.parentMediaOwnerOrgId || null;
      if (params.parentMediaOwnerOrgId) {
        try {
          const moOrg = await getDoc(doc(db, ORGANIZATIONSPATH, params.parentMediaOwnerOrgId));
          linkPayload.parentMediaOwnerName = moOrg.data()?.name || null;
        } catch {/* ignore */}
      }
      linkPayload.acceptedLanguages = Array.isArray(params.acceptedLanguages) ? params.acceptedLanguages : [];
    }
    await setDoc(linkRef, linkPayload);
    linkRefId = linkRef.id;
  } else {
    const globalColl = orgCategoryToGlobalCollection(params.category);
    if (globalColl) {
      const linkRef = doc(collection(db, globalColl));
      // Shape matches Firebase.createGlobalEntryForOrg so dropdowns that
      // read these collections don't need to special-case GOM-created rows.
      // `name` is included alongside `label` because the RapidUpload agency
      // autocomplete reads `data.name` specifically.
      await setDoc(linkRef, {
        label: trimmedName,
        name: trimmedName,
        value: slugify(trimmedName),
        linkedOrgId: orgId,
        createdAt: Date.now(),
      });
      linkRefId = linkRef.id;
    }
  }

  // 3) Assign the chosen user as the org ADMIN (members subcollection +
  //    /users/{uid}.organizations[]).
  await assignOwner(db, {
    orgId,
    orgName: trimmedName,
    orgCategory: params.category,
    ownerUid: params.ownerUid,
    ownerName: params.ownerName,
    actorUid: params.actorUid,
    actorName: params.actorName,
    skipChangeLog: true, // single log entry covers the whole create
  });

  // 4) Change log.
  const action = params.kind === 'broadcaster'
    ? 'broadcaster.create'
    : params.kind === 'mediaowner'
      ? 'mediaowner.create'
      : 'org.create';
  // Broadcaster-only fields are omitted entirely for mediaowner/org kinds
  // — Firestore rejects `undefined` values, so leaving them in throws
  // "Unsupported field value: undefined".
  const after: Record<string, unknown> = { category: params.category };
  if (params.kind === 'broadcaster') {
    after.parentMediaOwnerOrgId = params.parentMediaOwnerOrgId || null;
    after.languages = Array.isArray(params.acceptedLanguages) ? params.acceptedLanguages : [];
  }
  await writeChangeLog(db, {
    action,
    orgId,
    orgName: trimmedName,
    after,
    actorUid: params.actorUid,
    actorName: params.actorName || null,
    message: `Created ${params.kind} "${trimmedName}" and assigned owner.`,
  });

  return { orgId, linkId: linkRefId };
}

/** Renames an org. Updates the /organizations doc, the matching link doc's
 *  label/value, and every /users/{uid}.organizations[] entry's orgName
 *  snapshot. Mirrors Firebase.ts updateOrganizationName but also touches
 *  the link doc and emits a change-log entry. */
export async function renameOrg(db: any, params: {
  orgId: string;
  newName: string;
  actorUid: string;
  actorName?: string;
}): Promise<void> {
  const newName = String(params.newName).replace(/\s+/g, ' ').trim();
  if (!newName) throw new Error('Name is required');

  const orgRef = doc(db, ORGANIZATIONSPATH, params.orgId);
  const before = (await getDoc(orgRef)).data() || {};
  if (before.name === newName) return; // no-op

  await updateDoc(orgRef, { name: newName });

  // Update broadcaster + mediaowner link docs whose linkedOrgId matches.
  for (const linkColl of [BROADCASTERPATH, MEDIAOWNERPATH]) {
    const links = await getDocs(query(collection(db, linkColl), where('linkedOrgId', '==', params.orgId)));
    for (const d of links.docs) {
      await updateDoc(d.ref, { label: newName, value: slugify(newName) });
    }
  }

  // Update the global lookup entries (/agency, /artists-global, /client) whose
  // linkedOrgId matches. These back the campaign studio/agency + client
  // dropdowns (read as `displayName || label || name`), the RapidUpload agency
  // picker (reads `name`), etc. Previously omitted here, so renaming an
  // agency/studio/brand org left the dropdowns showing the old name.
  for (const globalColl of [AGENCYPATH, GLOBAL_ARTISTPATH, CLIENTPATH]) {
    const links = await getDocs(query(collection(db, globalColl), where('linkedOrgId', '==', params.orgId)));
    for (const d of links.docs) {
      await updateDoc(d.ref, { label: newName, name: newName, value: slugify(newName) });
    }
  }

  // Update parentMediaOwnerName on any broadcaster that points at this org.
  const bcParents = await getDocs(query(collection(db, BROADCASTERPATH), where('parentMediaOwnerOrgId', '==', params.orgId)));
  for (const d of bcParents.docs) {
    await updateDoc(d.ref, { parentMediaOwnerName: newName });
  }

  // Update user array snapshots — same loop as Firebase.updateOrganizationName.
  const membersSnap = await getDocs(collection(db, `${ORGANIZATIONSPATH}/${params.orgId}/members`));
  for (const m of membersSnap.docs) {
    const userRef = doc(db, USERSPATH, m.id);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) continue;
    const userData = userSnap.data();
    const orgs = Array.isArray(userData.organizations) ? userData.organizations : [];
    const updated = orgs.map((o: any) => (o && o.orgId === params.orgId ? { ...o, orgName: newName } : o));
    await updateDoc(userRef, { organizations: updated });
  }

  await writeChangeLog(db, {
    action: 'org.rename',
    orgId: params.orgId,
    orgName: newName,
    before: { name: before.name },
    after: { name: newName },
    actorUid: params.actorUid,
    actorName: params.actorName || null,
    message: `Renamed "${before.name || params.orgId}" → "${newName}".`,
  });
}

/** Changes a broadcaster's parent media owner. Updates the /broadcaster
 *  link doc only — parent is tracked there, not on the org doc. */
export async function changeBroadcasterParent(db: any, params: {
  linkId: string;
  newParentOrgId: string | null;
  actorUid: string;
  actorName?: string;
}): Promise<void> {
  const linkRef = doc(db, BROADCASTERPATH, params.linkId);
  const cur = (await getDoc(linkRef)).data() || {};
  if (cur.parentMediaOwnerOrgId === (params.newParentOrgId || null)) return;

  let parentName: string | null = null;
  if (params.newParentOrgId) {
    try {
      const moOrg = await getDoc(doc(db, ORGANIZATIONSPATH, params.newParentOrgId));
      parentName = moOrg.data()?.name || null;
    } catch {/* ignore */}
  }

  await updateDoc(linkRef, {
    parentMediaOwnerOrgId: params.newParentOrgId,
    parentMediaOwnerName: parentName,
  });

  await writeChangeLog(db, {
    action: 'broadcaster.parentChange',
    orgId: cur.linkedOrgId || null,
    orgName: cur.label || null,
    parentOrgId: params.newParentOrgId,
    parentOrgName: parentName,
    before: { parentMediaOwnerOrgId: cur.parentMediaOwnerOrgId, parentMediaOwnerName: cur.parentMediaOwnerName },
    after: { parentMediaOwnerOrgId: params.newParentOrgId, parentMediaOwnerName: parentName },
    actorUid: params.actorUid,
    actorName: params.actorName || null,
    message: `Re-parented "${cur.label || ''}" → ${parentName || '∅ (no media owner)'}.`,
  });
}

/** Updates a broadcaster's acceptedLanguages array (set, not union). */
export async function updateAcceptedLanguages(db: any, params: {
  linkId: string;
  languages: string[];
  actorUid: string;
  actorName?: string;
}): Promise<void> {
  const linkRef = doc(db, BROADCASTERPATH, params.linkId);
  const cur = (await getDoc(linkRef)).data() || {};
  const next = Array.isArray(params.languages) ? params.languages : [];
  const prev = Array.isArray(cur.acceptedLanguages) ? cur.acceptedLanguages : [];
  if (next.length === prev.length && next.every((l) => prev.includes(l))) return;
  await updateDoc(linkRef, { acceptedLanguages: next });
  await writeChangeLog(db, {
    action: 'org.languagesUpdate',
    orgId: cur.linkedOrgId || null,
    orgName: cur.label || null,
    before: { languages: prev },
    after: { languages: next },
    actorUid: params.actorUid,
    actorName: params.actorName || null,
    message: `Updated languages on "${cur.label || ''}": [${prev.join(',') || '∅'}] → [${next.join(',') || '∅'}].`,
  });
}

/**
 * Changes the org's subcategory and (automatically) the derived top-level
 * type. We always co-update both to keep the data consistent — the rest of
 * the app uses `categoryToType()` to enforce that mapping, and storing them
 * out of sync would break permission/feature checks downstream.
 */
export async function changeOrgCategory(db: any, params: {
  orgId: string;
  newCategory: OrgCategory;
  actorUid: string;
  actorName?: string;
}): Promise<void> {
  const orgRef = doc(db, ORGANIZATIONSPATH, params.orgId);
  const cur = (await getDoc(orgRef)).data() || {};
  const newType = categoryToType(params.newCategory);
  if (cur.category === params.newCategory && cur.type === newType) return;
  await updateDoc(orgRef, { category: params.newCategory, type: newType });

  // Cascade to each member's /users/{uid}.organizations[] entry so the
  // store-side snapshots match the canonical doc. Picker UI / dashboard
  // routing reads these fields.
  const membersSnap = await getDocs(collection(db, `${ORGANIZATIONSPATH}/${params.orgId}/members`));
  for (const m of membersSnap.docs) {
    const userRef = doc(db, USERSPATH, m.id);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) continue;
    const userData = userSnap.data();
    const orgs = Array.isArray(userData.organizations) ? userData.organizations : [];
    const updated = orgs.map((o: any) => (o && o.orgId === params.orgId
      ? { ...o, orgCategory: params.newCategory, orgType: newType }
      : o));
    await updateDoc(userRef, { organizations: updated });
  }

  await writeChangeLog(db, {
    action: 'org.typeChange',
    orgId: params.orgId,
    orgName: cur.name || null,
    before: { type: cur.type, category: cur.category },
    after: { type: newType, category: params.newCategory },
    actorUid: params.actorUid,
    actorName: params.actorName || null,
    message: `Changed category on "${cur.name || params.orgId}": ${cur.category || '∅'} → ${params.newCategory} (type → ${newType}).`,
  });
}

/** Assigns a user as ADMIN on an org: writes the member doc and appends to
 *  the user's organizations[] array. Used both during create and when
 *  adding an additional ADMIN later. Returns `changed: false` when the user
 *  was already ADMIN on the org (true no-op — neither a write nor a
 *  change-log entry happens). */
export async function assignOwner(db: any, params: {
  orgId: string;
  orgName: string;
  orgCategory: string;
  ownerUid: string;
  ownerName?: string;
  actorUid: string;
  actorName?: string;
  skipChangeLog?: boolean;
}): Promise<{ changed: boolean }> {
  const memberRef = doc(db, ORGANIZATIONSPATH, params.orgId, 'members', params.ownerUid);
  const memberSnap = await getDoc(memberRef);
  const now = new Date().toISOString();

  let memberWritten = false;
  if (!memberSnap.exists()) {
    await setDoc(memberRef, {
      joinedAt: now,
      role: 'ADMIN',
      invitedBy: params.actorUid,
      inviteCode: null,
    });
    memberWritten = true;
  } else if (memberSnap.data()?.role !== 'ADMIN') {
    await updateDoc(memberRef, { role: 'ADMIN' });
    memberWritten = true;
  }

  // Append/upgrade the user's organizations[] entry.
  let userArrayWritten = false;
  const userRef = doc(db, USERSPATH, params.ownerUid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    const data = userSnap.data();
    const orgs = Array.isArray(data.organizations) ? data.organizations : [];
    const exists = orgs.find((o: any) => o && o.orgId === params.orgId);
    if (!exists) {
      orgs.push({
        orgId: params.orgId,
        orgName: params.orgName,
        orgCategory: params.orgCategory,
        orgType: 'broadcaster',
        role: 'ADMIN',
        joinedAt: now,
      });
      await updateDoc(userRef, { organizations: orgs });
      userArrayWritten = true;
    } else if (exists.role !== 'ADMIN') {
      const next = orgs.map((o: any) => (o && o.orgId === params.orgId ? { ...o, role: 'ADMIN' } : o));
      await updateDoc(userRef, { organizations: next });
      userArrayWritten = true;
    }
  }

  const changed = memberWritten || userArrayWritten;

  // Only log when something actually changed — re-saving the same ADMIN is
  // a complete no-op (no docs written, no log entry). Keeps the audit trail
  // clean of phantom "assigned" events when the dropdown is left selected.
  if (changed && !params.skipChangeLog) {
    await writeChangeLog(db, {
      action: 'owner.assign',
      orgId: params.orgId,
      orgName: params.orgName,
      after: { ownerUid: params.ownerUid, ownerName: params.ownerName },
      actorUid: params.actorUid,
      actorName: params.actorName || null,
      message: `Added ${params.ownerName || params.ownerUid} as ADMIN of "${params.orgName}".`,
    });
  }

  return { changed };
}

/** Deletes an entity. For media owners, orphans every broadcaster that
 *  was parented to it (sets parentMediaOwnerOrgId/Name to null). Removes
 *  the link doc, the org's members subcollection, the org doc itself, and
 *  every user's organizations[] entry. */
export async function deleteEntity(db: any, params: {
  kind: EntityKind;
  orgId: string;
  linkId: string;
  actorUid: string;
  actorName?: string;
}): Promise<{ orphanedBroadcasters: number; removedUserMemberships: number }> {
  const orgRef = doc(db, ORGANIZATIONSPATH, params.orgId);
  const orgData = (await getDoc(orgRef)).data() || {};
  const orgName = orgData.name || '';
  let orphanedBroadcasters = 0;
  let removedUserMemberships = 0;

  // Media owner: orphan broadcasters parented to this org.
  if (params.kind === 'mediaowner') {
    const bcSnap = await getDocs(query(collection(db, BROADCASTERPATH), where('parentMediaOwnerOrgId', '==', params.orgId)));
    for (const d of bcSnap.docs) {
      await updateDoc(d.ref, { parentMediaOwnerOrgId: null, parentMediaOwnerName: null });
      orphanedBroadcasters++;
    }
  }

  // Delete member subcollection + matching user array entries.
  const membersSnap = await getDocs(collection(db, `${ORGANIZATIONSPATH}/${params.orgId}/members`));
  for (const m of membersSnap.docs) {
    await deleteDoc(m.ref);
    const userRef = doc(db, USERSPATH, m.id);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      const orgs = Array.isArray(data.organizations) ? data.organizations : [];
      const next = orgs.filter((o: any) => !o || o.orgId !== params.orgId);
      if (next.length !== orgs.length) {
        await updateDoc(userRef, { organizations: next });
        removedUserMemberships++;
      }
    }
  }

  // Delete the link doc. For broadcaster/mediaowner this lives in
  // /broadcaster or /mediaowner. For 'org' kind it lives in /agency,
  // /client, or /artists-global depending on the org's category — we
  // look it up by linkedOrgId rather than relying on params.linkId since
  // GOM rows for "other orgs" don't always carry a populated linkId.
  if (params.kind !== 'org' && params.linkId) {
    const linkColl = params.kind === 'broadcaster' ? BROADCASTERPATH : MEDIAOWNERPATH;
    await deleteDoc(doc(db, linkColl, params.linkId));
  } else if (params.kind === 'org') {
    const globalColl = orgCategoryToGlobalCollection(orgData.category || '');
    if (globalColl) {
      const linkSnap = await getDocs(query(
        collection(db, globalColl),
        where('linkedOrgId', '==', params.orgId),
      ));
      for (const d of linkSnap.docs) await deleteDoc(d.ref);
    }
  }

  // Finally delete the org doc.
  await deleteDoc(orgRef);

  const deleteAction = params.kind === 'broadcaster'
    ? 'broadcaster.delete'
    : params.kind === 'mediaowner'
      ? 'mediaowner.delete'
      : 'org.delete';
  await writeChangeLog(db, {
    action: deleteAction,
    orgId: params.orgId,
    orgName,
    before: { kind: params.kind, name: orgName, orphanedBroadcasters, removedUserMemberships },
    actorUid: params.actorUid,
    actorName: params.actorName || null,
    message: `Deleted ${params.kind} "${orgName}"${params.kind === 'mediaowner' && orphanedBroadcasters > 0 ? ` (${orphanedBroadcasters} broadcaster(s) orphaned)` : ''}.`,
  });

  return { orphanedBroadcasters, removedUserMemberships };
}
